import React, { useState } from 'react';
import SEO from '@components/SEO';
import {
  TextField,
  Button,
  Avatar,
  Link,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { Styles } from './styles';
import LogoElitePrime from '@assets/images/Logotipo/Elite Prime_Logo_FundoPreto.png';
import { useUserStore } from '@stores/User/User';
import { useMetadataStore } from '@stores/Metadata/Metadata';

type View = 'login' | 'forgot' | 'sent';

const LoginScreen: React.FC = () => {
  // ─── Login state ──────────────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ─── Forgot password state ────────────────────────────────────────────────
  const [view, setView] = useState<View>('login');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  const navigate = useNavigate();
  const { logIn, forgotPassword } = useUserStore();
  const { configuracoes } = useMetadataStore();

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const success = await logIn(email, password, rememberMe);
    if (success) {
      navigate('/area-do-corretor');
    } else {
      setErrorMessage('Login falhou. Verifique suas credenciais.');
    }
  };

  const handleForgotSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!recoveryEmail.trim()) {
      setRecoveryError('Informe um e-mail válido.');
      return;
    }
    setRecoveryLoading(true);
    setRecoveryError('');
    const success = await forgotPassword(recoveryEmail.trim());
    setRecoveryLoading(false);
    if (success) {
      setView('sent');
    } else {
      setRecoveryError(
        'Não foi possível processar a solicitação. Tente novamente.'
      );
    }
  };

  const goBackToLogin = () => {
    setView('login');
    setRecoveryEmail('');
    setRecoveryError('');
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Styles.PageContainer>
      <SEO title="Login" noIndex />
      <Styles.LeftPanel>
        <Styles.LogoContainer>
          <img
            src={LogoElitePrime}
            alt="Elite Prime"
            style={{ height: '150px', width: 'auto', backgroundColor: 'black' }}
          />
        </Styles.LogoContainer>
        <Styles.ImageOverlay />
      </Styles.LeftPanel>

      <Styles.RightPanel>
        <Styles.FormSection>
          <Styles.FormContainer>
            {/* ── LOGIN ─────────────────────────────────────────────────── */}
            {view === 'login' && (
              <>
                <Avatar sx={{ m: 1, bgcolor: '#105330' }}>
                  <LockOutlinedIcon />
                </Avatar>
                <Typography
                  component="h1"
                  variant="h5"
                  sx={{ color: '#105330', fontWeight: 'bold' }}>
                  ACESSO RESTRITO
                </Typography>
                <Styles.Form onSubmit={handleSubmit}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="E-mail"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="outlined"
                    sx={Styles.TextField}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Senha"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    variant="outlined"
                    sx={Styles.TextField}
                  />
                  {!!errorMessage?.length && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: 2, textAlign: 'center' }}>
                      {errorMessage}
                    </Typography>
                  )}
                  <Styles.OptionsContainer>
                    <FormControlLabel
                      control={
                        <Checkbox
                          value={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          color="primary"
                          sx={Styles.Checkbox}
                        />
                      }
                      label="Lembrar-me"
                    />
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setView('forgot')}
                      sx={{
                        color: '#105330',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}>
                      Esqueceu a senha?
                    </Link>
                  </Styles.OptionsContainer>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={Styles.SubmitButton}>
                    ENTRAR
                  </Button>
                </Styles.Form>
              </>
            )}

            {/* ── RECUPERAR SENHA ───────────────────────────────────────── */}
            {view === 'forgot' && (
              <>
                <Avatar sx={{ m: 1, bgcolor: '#105330' }}>
                  <LockResetIcon />
                </Avatar>
                <Typography
                  component="h1"
                  variant="h5"
                  sx={{
                    color: '#105330',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                  RECUPERAR SENHA
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
                  Informe o e-mail cadastrado. Uma nova senha será gerada e
                  enviada para você.
                </Typography>
                <Styles.Form onSubmit={handleForgotSubmit}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="recovery-email"
                    label="E-mail"
                    name="recovery-email"
                    autoComplete="email"
                    autoFocus
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    variant="outlined"
                    sx={Styles.TextField}
                    disabled={recoveryLoading}
                  />
                  {!!recoveryError && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: 1, textAlign: 'center' }}>
                      {recoveryError}
                    </Typography>
                  )}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={recoveryLoading}
                    sx={{ ...Styles.SubmitButton, mt: 3, mb: 1 }}>
                    {recoveryLoading ? (
                      <CircularProgress size={24} sx={{ color: '#fff' }} />
                    ) : (
                      'RECUPERAR SENHA'
                    )}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={goBackToLogin}
                    disabled={recoveryLoading}
                    sx={{ color: '#105330', textTransform: 'none' }}>
                    Voltar para o login
                  </Button>
                </Styles.Form>
              </>
            )}

            {/* ── E-MAIL ENVIADO ────────────────────────────────────────── */}
            {view === 'sent' && (
              <>
                <Avatar sx={{ m: 1, bgcolor: '#2FB674' }}>
                  <CheckCircleOutlineIcon />
                </Avatar>
                <Typography
                  component="h1"
                  variant="h5"
                  sx={{
                    color: '#105330',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                  VERIFIQUE SEU E-MAIL
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, mb: 3, textAlign: 'center', lineHeight: 1.7 }}>
                  Se o endereço <strong>{recoveryEmail}</strong> estiver
                  cadastrado e ativo, você receberá uma nova senha em breve.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={goBackToLogin}
                  sx={Styles.SubmitButton}>
                  VOLTAR PARA O LOGIN
                </Button>
              </>
            )}

            <Styles.FooterContainer>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {'© '}
                <Link color="inherit" href="/home">
                  {configuracoes?.nome_empresa || 'Elite Prime Imobiliária'}
                </Link>{' '}
                {new Date().getFullYear()}
              </Typography>
            </Styles.FooterContainer>
          </Styles.FormContainer>
        </Styles.FormSection>
      </Styles.RightPanel>
    </Styles.PageContainer>
  );
};

export default LoginScreen;
